import { error } from '@asset-metrix/portal-auth';
import { HttpResponse } from '@asset-metrix/portal-gateway-client-js';

import { HTTP_STATUS } from '../utils/constants';

export class HttpError extends Error {
  readonly statusCode: number;
  readonly data?: unknown;

  constructor(message: string, response: HttpResponse<unknown, unknown>) {
    super(message);
    this.statusCode = response.status;
    this.data = response.error;
  }
}

export class MaintenanceModeActiveError extends Error {
  constructor(message?: string) {
    super(message || 'Maintenance mode is currently active');
  }
}

/**
 * Checks if an error is a PortalAuthHttpError with SERVICE_UNAVAILABLE status (503)
 */
export function isPortalAuthServiceUnavailableError(
  err: unknown
): err is Error {
  return (
    err instanceof error.PortalAuthHttpError &&
    err.statusCode === HTTP_STATUS.SERVICE_UNAVAILABLE
  );
}

/**
 * Checks if an error is a PortalAuthHttpError with NETWORK_ERROR status (0) or a TypeError from a failed fetch
 */
export function isPortalAuthNetworkError(err: unknown): err is Error {
  if (err instanceof error.PortalAuthHttpError) {
    return err.statusCode === HTTP_STATUS.NETWORK_ERROR;
  }

  if (err instanceof TypeError) {
    return (
      err.message.startsWith('Failed to fetch') ||
      err.message.startsWith('fetch failed')
    );
  }

  return false;
}

/**
 * Checks if an error is a HttpError with SERVICE_UNAVAILABLE status (503)
 */
export function isServiceUnavailableError(err: unknown): err is Error {
  return (
    err instanceof HttpError &&
    err.statusCode === HTTP_STATUS.SERVICE_UNAVAILABLE
  );
}

/**
 * Checks if an error is a HttpError with NETWORK_ERROR status (0) or a TypeError from a failed fetch
 */
export function isNetworkError(err: unknown): err is Error {
  if (err instanceof HttpError) {
    return err.statusCode === HTTP_STATUS.NETWORK_ERROR;
  }

  if (err instanceof TypeError) {
    return (
      err.message.includes('Failed to fetch') ||
      err.message.includes('fetch failed')
    );
  }

  return false;
}

/**
 * checks if a environment is scheduled for planned maintenance
 */
export function isMaintenanceModeEnabled(err: unknown): err is Error {
  if (
    err instanceof HttpError &&
    err.statusCode === HTTP_STATUS.SERVICE_UNAVAILABLE
  ) {
    const errorData = err.data as { code: string };
    if (errorData?.code === 'maintenance-mode-enabled') {
      return true;
    }
  }

  if (err instanceof MaintenanceModeActiveError) {
    return true;
  }

  return false;
}

export function isAbortError(err: unknown): err is Error {
  return err instanceof Error && err.name === 'AbortError';
}
